/**
 * FiasAIAgentEvents — renders the agent activity inside one assistant
 * message: the tool activity feed, clarifying-question cards, explicit
 * confirmation cards (exact-payload preview + Approve/Reject), and
 * UI-action chips.
 */

import {
  Check,
  CircleAlert,
  CircleHelp,
  Clock,
  ExternalLink,
  Loader2,
  MousePointerClick,
  Sparkles,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { AgentEvent } from './agent-types';
import { refOpenHandoff, type ArcheHandoffPayload } from './handoff';
import { humanizeLabel } from './lib/humanizeLabel';

interface FiasAIAgentEventsProps {
  events: AgentEvent[];
  /** Approve/Reject the pending confirmation (only the pending one is actionable). */
  onRespondConfirmation: (interactionId: string, approved: boolean, feedback?: string) => void;
  /**
   * Open the approval window for the pending confirmation (review + edit +
   * approve/reject happen there). Absent ⇒ the inline card can't reopen it.
   */
  onReviewConfirmation?: (interactionId: string) => void;
  /** Answer a pending clarifying question (choice buttons; free text uses the main input). */
  onAnswerQuestion: (answer: string) => void;
  pendingConfirmationId: string | null;
  pendingQuestionId: string | null;
  /**
   * Open a tool-result ref as an Arche Handoff (typically the context's
   * `openHandoff`). When absent, refs render as plain labels — refs whose
   * type has no registry entry always do.
   */
  onOpenRef?: (payload: ArcheHandoffPayload) => void;
}

export function FiasAIAgentEvents({
  events,
  onRespondConfirmation,
  onReviewConfirmation,
  onAnswerQuestion,
  pendingConfirmationId,
  pendingQuestionId,
  onOpenRef,
}: FiasAIAgentEventsProps) {
  if (events.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5 mb-2" data-testid="fias-ai-agent-events">
      {events.map((event) => {
        switch (event.kind) {
          case 'tool':
            return <ToolEventRow key={event.id} event={event} onOpenRef={onOpenRef} />;
          case 'clarification':
            return (
              <ClarificationCard
                key={event.id}
                event={event}
                isPending={event.interactionId === pendingQuestionId}
                onAnswer={onAnswerQuestion}
              />
            );
          case 'confirmation':
            return (
              <ConfirmationCard
                key={event.id}
                event={event}
                isPending={event.interactionId === pendingConfirmationId}
                onRespond={onRespondConfirmation}
                onReview={onReviewConfirmation}
              />
            );
          case 'ui_action':
            return <UiActionRow key={event.id} event={event} />;
        }
      })}
    </div>
  );
}

function ToolEventRow({
  event,
  onOpenRef,
}: {
  event: Extract<AgentEvent, { kind: 'tool' }>;
  onOpenRef?: (payload: ArcheHandoffPayload) => void;
}) {
  return (
    <div className="flex items-start gap-2 text-xs text-muted-foreground px-2 py-1 rounded-md bg-muted/40">
      {event.status === 'running' ? (
        <Loader2 className="h-3.5 w-3.5 mt-0.5 animate-spin flex-shrink-0" />
      ) : event.status === 'done' ? (
        <Check className="h-3.5 w-3.5 mt-0.5 text-green-600 flex-shrink-0" />
      ) : event.status === 'awaiting' ? (
        <Clock className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
      ) : (
        <CircleAlert className="h-3.5 w-3.5 mt-0.5 text-destructive flex-shrink-0" />
      )}
      <div className="min-w-0">
        <span className="font-medium text-foreground/80">{event.description}</span>
        {event.status === 'awaiting' && (
          <span className="ml-1 italic">— waiting for your approval</span>
        )}
        {event.summary && <span className="ml-1">{event.summary}</span>}
        {event.refs && event.refs.length > 0 && (
          <span className="ml-1 inline-flex flex-wrap items-center gap-1 align-middle">
            {event.refs.map((r) => {
              // Openable refs (registry match) render as click-through chips;
              // everything else stays a plain label.
              const handoff = onOpenRef ? refOpenHandoff(r) : null;
              return handoff ? (
                <button
                  key={`${r.type}:${r.id}`}
                  type="button"
                  className="inline-flex items-center gap-0.5 rounded border border-border bg-background px-1.5 py-0.5 text-foreground/80 hover:bg-accent hover:text-foreground transition-colors"
                  onClick={() => onOpenRef?.(handoff)}
                  data-testid="fias-ai-ref-chip"
                >
                  {r.label}
                  <ExternalLink className="h-3 w-3 opacity-60" />
                </button>
              ) : (
                <span key={`${r.type}:${r.id}`} className="text-foreground/60">
                  {r.label}
                </span>
              );
            })}
          </span>
        )}
      </div>
    </div>
  );
}

function ClarificationCard({
  event,
  isPending,
  onAnswer,
}: {
  event: Extract<AgentEvent, { kind: 'clarification' }>;
  isPending: boolean;
  onAnswer: (answer: string) => void;
}) {
  return (
    <div className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
      <div className="flex items-center gap-2 font-medium">
        <CircleHelp className="h-4 w-4 text-primary flex-shrink-0" />
        {event.question}
      </div>
      {event.answer ? (
        <div className="mt-1 text-xs text-muted-foreground">Answered: {event.answer}</div>
      ) : isPending ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(event.choices ?? []).map((choice) => (
            <Button
              key={choice}
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => onAnswer(choice)}
            >
              {choice}
            </Button>
          ))}
          <span className="text-xs text-muted-foreground self-center">
            {event.choices?.length ? 'or type your answer below' : 'Type your answer below'}
          </span>
        </div>
      ) : (
        <div className="mt-1 text-xs text-muted-foreground">No longer awaiting an answer.</div>
      )}
    </div>
  );
}

function ConfirmationCard({
  event,
  isPending,
  onRespond,
  onReview,
}: {
  event: Extract<AgentEvent, { kind: 'confirmation' }>;
  isPending: boolean;
  onRespond: (interactionId: string, approved: boolean, feedback?: string) => void;
  onReview?: (interactionId: string) => void;
}) {
  const title = humanizeLabel(event.preview.title);

  // Settled record — a clean, non-technical line in the transcript, followed by
  // WHAT was approved (destination + the message/fields) so the history is an
  // audit trail, not just "something was approved". editable values fold in any
  // in-card edit (they reflect what actually executed). Persisted server-side
  // (metadata.settledActions) so this survives a conversation reload.
  if (event.status !== 'pending' || !isPending) {
    const details: Array<{ label: string; value: string }> = [
      ...event.preview.fields,
      ...(event.preview.editable?.map((f) => ({ label: f.label, value: f.value })) ?? []),
      // Drop raw id-plumbing fields (e.g. `recipientUserId`, `channelId`) — a
      // resolved field ("Send to: DM with …") already covers them, and a raw id
      // is exactly the "too technical" noise this record is meant to avoid. The
      // reloaded record is already server-cleaned, so this only trims the live one.
    ].filter((d) => d.value !== '' && !/id$/i.test(d.label));
    const ran = event.status === 'approved' || event.status === 'rejected';
    return (
      <div
        className="rounded-md bg-muted/40 px-2 py-1 text-xs"
        data-testid="fias-ai-confirmation-card"
      >
        <div className="flex items-center gap-2">
          {event.status === 'approved' ? (
            <>
              <Check className="h-3.5 w-3.5 flex-shrink-0 text-green-600" />
              <span className="font-medium text-foreground/80">Approved</span>
              <span className="text-muted-foreground">· {title}</span>
            </>
          ) : event.status === 'rejected' ? (
            <>
              <X className="h-3.5 w-3.5 flex-shrink-0 text-destructive" />
              <span className="font-medium text-foreground/80">Rejected</span>
              <span className="text-muted-foreground">· {title}</span>
            </>
          ) : event.status === 'expired' ? (
            <span className="text-muted-foreground">Expired — {title} was never run</span>
          ) : (
            <span className="text-muted-foreground">Superseded — {title} was never run</span>
          )}
        </div>
        {ran && details.length > 0 && (
          <dl className="mt-1 space-y-0.5 pl-5">
            {details.map((d, i) => (
              <div key={`${d.label}-${i}`} className="flex gap-2">
                <dt className="min-w-20 flex-shrink-0 text-muted-foreground">
                  {humanizeLabel(d.label)}
                </dt>
                <dd className="min-w-0 break-words text-foreground/80">{d.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    );
  }

  // Pending — the compact prompt. Review/edit/approve happen in the window.
  return (
    <div
      className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm"
      data-testid="fias-ai-confirmation-card"
    >
      <div className="flex items-center gap-2 font-medium">
        <Sparkles className="h-4 w-4 flex-shrink-0 text-primary" />
        <span>
          Fias needs your approval to <span className="font-semibold">{title.toLowerCase()}</span>
        </span>
      </div>
      <div className="mt-2 flex gap-2">
        <Button size="sm" className="h-7 text-xs" onClick={() => onReview?.(event.interactionId)}>
          Review &amp; approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={() => onRespond(event.interactionId, false)}
        >
          <X className="mr-1 h-3.5 w-3.5" /> Reject
        </Button>
      </div>
    </div>
  );
}

function UiActionRow({ event }: { event: Extract<AgentEvent, { kind: 'ui_action' }> }) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground px-2 py-1 rounded-md bg-muted/40">
      <MousePointerClick className="h-3.5 w-3.5 flex-shrink-0" />
      <span>
        {event.status === 'applied' && (
          <>
            Applied <span className="font-medium text-foreground/80">{event.description}</span> in
            the open arche
          </>
        )}
        {event.status === 'failed' && (
          <>
            Could not apply{' '}
            <span className="font-medium text-foreground/80">{event.description}</span>
          </>
        )}
        {event.status === 'stale' && (
          <>
            <span className="font-medium text-foreground/80">{event.description}</span> targeted an
            arche that is no longer open — not applied
          </>
        )}
      </span>
    </div>
  );
}
